import axios from 'axios';
import React from 'react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TYPES = { maturity: 'digital', 'data-maturity': 'data', 'ai-maturity': 'ai', bpcm: 'bpcm', sustainability: 'sustainability', 'cyber-security': 'cyber' };
const pendingKey = kind => `samanvai-pending-${kind}`;
const failure = message => Object.assign(new Error(message), { response: { data: { detail: message } } });

async function followJob(kind, jobId, { navigate = false } = {}) {
  const end = Date.now() + 540000;
  const toastId = `assessment-${kind}`;
  while (Date.now() < end) {
    let data;
    try { ({ data } = await axios.get(`${API}/assessment-jobs/${jobId}`, { timeout: 15000 })); }
    catch (error) {
      if (error.response?.status === 404) throw failure('Saved assessment request not found.');
      await new Promise(resolve => setTimeout(resolve, 3000));
      continue;
    }
    toast.loading(data.stage || 'Preparing assessment', { id: toastId });
    if (data.status === 'complete') {
      sessionStorage.removeItem(pendingKey(kind));
      toast.dismiss(toastId);
      const id = data.result.assessment_id || data.result.id;
      const url = new URL(window.location.href);
      url.searchParams.set('id', id);
      if (navigate) window.location.assign(url.toString());
      else {
        window.history.replaceState(null, '', url.toString());
        window.dispatchEvent(new Event('assessment-ready'));
      }
      return { data: data.result, status: 200 };
    }
    if (['error', 'interrupted'].includes(data.status)) {
      toast.dismiss(toastId);
      throw failure(data.error || 'Assessment could not complete. Your answers are saved.');
    }
    await new Promise(resolve => setTimeout(resolve, 3000));
  }
  toast.dismiss(toastId);
  throw failure('Processing is taking longer than expected. Your request is saved; resume it rather than creating another.');
}

export async function submitMaturity(url, formData) {
  const type = new URL(url).pathname.split('/').at(-2);
  const kind = TYPES[type];
  let response;
  // Retrying a lost submission is safe: backend fingerprints the actual answers.
  for (let attempt = 0; attempt < 2; attempt++) {
    try { response = await axios.post(url, formData, { timeout: 20000 }); break; }
    catch (error) {
      if (error.response?.status < 500 || attempt) {
        const detail = error.response?.data?.detail;
        throw failure(Array.isArray(detail) ? detail.map(d => d.msg || 'Invalid input').join('; ') :
          typeof detail === 'string' ? detail : 'Could not contact the assessment service. Please resume your saved request.');
      }
    }
  }
  if (response.status !== 202) {
    const id = response.data.assessment_id || response.data.id;
    if (id) {
      const current = new URL(window.location.href);
      current.searchParams.set('id', id);
      window.history.replaceState(null, '', current.toString());
      window.dispatchEvent(new Event('assessment-ready'));
    }
    return response;
  }
  const jobId = response.data.job_id;
  sessionStorage.setItem(pendingKey(kind), jobId);
  if (['error', 'interrupted'].includes(response.data.status)) {
    await axios.post(`${API}/assessment-jobs/${jobId}/retry`, null, { timeout: 15000 });
  }
  return followJob(kind, jobId);
}

export function resumeMaturity(kind) {
  const id = sessionStorage.getItem(pendingKey(kind));
  if (!id || new URLSearchParams(window.location.search).has('id')) return;
  followJob(kind, id, { navigate: true }).catch(error => {
    toast.error(error.message, { id: `assessment-${kind}`, duration: Infinity,
      action: <button data-testid={`resume-${kind}-assessment`} className="font-semibold underline" onClick={async () => {
        await axios.post(`${API}/assessment-jobs/${id}/retry`);
        resumeMaturity(kind);
      }}>Resume saved request</button> });
  });
}